/* eslint-disable max-lines */
import { ICellRendererParams } from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Router, RouterOutlet } from '@angular/router';
import dayjs from 'dayjs';
import { cloneDeep, groupBy, last, sortBy, uniq } from 'lodash-es';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  EMPTY,
  forkJoin,
  from,
  merge,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import { BudgetService } from '@features/budget/services/budget.service';

import { ApprovalState } from '@models/approval/approval.model';
import { ApprovalQuery } from '@models/approval/approval.query';
import { BudgetState, ExtendedBudgetData } from '@models/budget/budget.model';
import { BudgetQuery } from '@models/budget/budget.query';

import { ProcessingLoaderComponent } from '@shared/components/processing-loader/processing-loader.component';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { ApiService } from '@shared/services/api.service';
import {
  AmountType,
  Approval,
  BudgetData,
  CategoryType,
  Currency,
  DocumentType,
  EventType,
  GqlService,
  InvoiceStatus,
  listDocumentsQuery,
  ListQuarterCloseExpensesInput,
  listQuarterCloseExpensesQuery,
  listTrialTimelineQuery,
  OrganizationType,
} from '@shared/services/gql.service';
import { injectFlags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { LoggedInUser } from '@shared/store/auth/LoggedInUser';
import { EventQuery } from '@shared/store/event/event.query';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceAmounts } from '@shared/store/invoice/invoice.model';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { MainService } from '@shared/store/main/main.service';
import { MainStore } from '@shared/store/main/main.store';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { CurrencyToggle, Option } from '@shared/types/components.type';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { signalRequestWithRefresh } from '@shared/utils/signal-request-with-refresh';
import { ObjectFromList, Utils } from '@shared/utils/utils';

import { PeriodCloseHeaderComponent } from './period-close-header/period-close-header.component';
import { QCGridData } from './period-close.model';

Utils.extendDayjs();

export interface InvestigatorEstimate {
  other: number;
  overhead: number;
  patient: number;
  show_adjustment?: boolean;
  total_all: number;
}

export type MonthCloseTableRowData = ExtendedBudgetData & {
  [attributes: `custom_attr_${string}`]: string;
  [permission: `${string}::${string}::disabled`]: boolean;
  [permission: `${string}::${string}`]: boolean;
  account_value: string;
  accrual_adjusted_obj: Record<string, number>;
  adjustment_obj: Record<string, number>;
  dept_value: string;
  ending_accruals: number;
  eom_accrual: Record<string, number>;
  eom_accrual_credit: Record<string, number>;
  eom_accrual_debit: Record<string, number>;
  eom_accruals: Record<string, number>;
  eom_prepaid: Record<string, number>;
  eom_prepaid_credit: Record<string, number>;
  eom_prepaid_debit: Record<string, number>;
  eom_unpaid: Record<string, number>;
  initial_wp_obj: Record<string, number>;
  invoice_amounts: Record<string, number>;
  invoice_ltd: number;
  invoice_ltd_end: number;
  invoice_ltd_start: number;
  invoice_unpaid_ltd_end: number;
  monthly_invoice_ltd: Record<string, number>;
  monthly_wp_obj: Record<string, number>;
  net_accruals: Record<string, number>;
  po_value: string;
  starting_accrual: number;
  starting_accruals: number;
  starting_prepaid: number;
  wp_ltd_end: number;
  wp_ltd_start: number;
};

export interface MonthStats {
  allOfThemClosed?: boolean;
  auditPackages?: listDocumentsQuery['items'];
  date: string;
  eom_accruals: number;
  status: 'Closed' | 'Future' | 'Open';
}

export interface QuarterDate {
  date: string;
  iso: string;
  parsedDate: dayjs.Dayjs;
}

export interface QuarterEndData {
  ending_accruals: number;
  invoice_ltd: number;
  invoice_unpaid_ltd: number;
  wp_ltd: number;
}

export interface QuarterStartData {
  invoice_ltd: number;
  starting_accrual: number;
  starting_prepaid: number;
  wp_ltd: number;
}

type MonthCloseBottomData = Pick<
  MonthCloseTableRowData,
  | 'account_value'
  | 'accrual_adjusted_obj'
  | 'adjustment_obj'
  | 'attributes'
  | 'dept_value'
  | 'eom_accrual'
  | 'eom_accrual_credit'
  | 'eom_accrual_debit'
  | 'eom_accruals'
  | 'eom_prepaid'
  | 'eom_prepaid_credit'
  | 'eom_prepaid_debit'
  | 'eom_unpaid'
  | 'forecast_obj'
  | 'initial_wp_obj'
  | 'invoice_amounts'
  | 'monthly_invoice_ltd'
  | 'monthly_wp_obj'
  | 'net_accruals'
  | 'po_value'
>;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ProcessingLoaderComponent, PeriodCloseHeaderComponent, RouterOutlet, AsyncPipe],
  selector: 'aux-period-close',
  styles: [
    `
      .month-name {
        font-size: 3.5rem;
        line-height: 3.5rem;
      }
    `,
  ],
  templateUrl: './period-close.component.html',
})
export class PeriodCloseComponent implements OnDestroy, OnInit {
  authQuery = inject(AuthQuery);
  budgetQuery = inject(BudgetQuery);
  destroyRef = inject(DestroyRef);
  globalStore = inject(GlobalStore);
  organizationQuery = inject(OrganizationQuery);

  private apiService = inject(ApiService);
  private approvalQuery = inject(ApprovalQuery);
  private authService = inject(AuthService);
  private budgetService = inject(BudgetService);
  private eventQuery = inject(EventQuery);
  private eventService = inject(EventService);
  private gqlService = inject(GqlService);
  private invoiceService = inject(InvoiceService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private mainService = inject(MainService);
  private mainStore = inject(MainStore);
  private organizationService = inject(OrganizationService);
  private organizationStore = inject(OrganizationStore);
  private overlayService = inject(OverlayService);
  private periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  private router = inject(Router);
  private workflowQuery = inject(WorkflowQuery);
  private workflowService = inject(WorkflowService);

  flags = injectFlags({
    isCurrencyToggleVisible: 'close_summary_currency_toggle',
  });

  currentQuarter = signal('');
  isVendorCurrenciesUSD = signal(false);
  monthClosed = signal(false);
  monthsStat = signal<MonthStats[]>([]);
  newQCGridDataLoading = signal(false);
  newQCGridDataWithoutTrialCurrency = signal<QCGridData[]>([]);
  numberOfVendorCurrencies = signal(0);
  quarters = signal<string[]>([]);
  // todo refactor move selectedQuarter to store and always use signal
  // Q1-2023
  selectedQuarterSignal = signal('');
  timeline = signalRequestWithRefresh<listTrialTimelineQuery[]>(
    of([]).pipe(
      switchMap(() => this.gqlService.listTrialTimeline$()),
      map(({ data, errors, success }) => {
        if (success && data) {
          return sortBy(data, 'timeline_month');
        }

        console.error(errors);

        return [];
      })
    ),
    []
  );

  invoicesLoading$ = new BehaviorSubject(false);

  loading$ = combineLatest([this.budgetQuery.selectLoading(), this.invoicesLoading$]).pipe(
    map((boolArr) => boolArr.some((bool) => bool))
  );

  invoices$ = new BehaviorSubject<
    Record<
      string,
      {
        accrual_period: string;
        expense_amounts: InvoiceAmounts;
        id: string;
        invoice_status: InvoiceStatus;
        organization_id: string;
        payment_date: string;
      }[]
    >
  >({});

  gridInit = combineLatest([
    this.loading$,
    this.budgetQuery.select(),
    this.approvalQuery.select(),
    this.invoices$,
  ]).pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap(([loading, budget_state, approval_state, groupedInvoices]) => {
      if (loading) {
        return EMPTY;
      }

      return from(this.authService.getLoggedInUser()).pipe(
        map((user) => {
          return {
            approval_state,
            budget_state,
            groupedInvoices,
            user,
          };
        })
      );
    })
  );

  gridData$ = this.gridInit.pipe(
    map((params) => this.mapFn(params)),
    shareReplay(1)
  );

  gridData = toSignal(this.gridData$, { initialValue: [] });

  newQCGridData$ = new BehaviorSubject<QCGridData[]>([]);

  newQCGridData = toSignal(this.newQCGridData$, { requireSync: true });

  currencyToggleLSKey = computed(() => {
    return `close_summary_currency_toggle_${this.mainQuery.selectedTrial()?.id || ''}`;
  });
  isCurrentQuarterSelected = computed(() => {
    return this.selectedQuarterSignal() === this.currentQuarter();
  });
  quarterOptions = computed(() => {
    return this.quarters().map<Option>((quarter) => {
      const formattedQuarter = quarter.replace('-', ' ');

      return {
        label: `${formattedQuarter}${quarter === this.currentQuarter() ? ' (Current)' : ''}`,
        value: quarter,
      };
    });
  });
  selectedQuarterMonths = computed(() => {
    const timeline = this.timeline.value();
    const selectedQuarter = this.selectedQuarterSignal();

    return timeline
      .filter(({ timeline_quarter }) => timeline_quarter === selectedQuarter.replace('-', ' '))
      .map(({ timeline_month }) => timeline_month);
  });
  showCurrencyToggle = computed(() => {
    return !this.isVendorCurrenciesUSD() && this.flags().isCurrencyToggleVisible;
  });

  currencyToggle = linkedSignal<CurrencyToggle>(() => {
    const storedValue =
      localStorage.getItem(this.currencyToggleLSKey()) || CurrencyToggle.CONTRACTED;

    if (this.showCurrencyToggle()) {
      return storedValue as CurrencyToggle;
    }

    return CurrencyToggle.CONTRACTED;
  });

  _syncCurrencyToggle = effect(() => {
    localStorage.setItem(this.currencyToggleLSKey(), this.currencyToggle());
  });
  auditPackages$ = new BehaviorSubject<listDocumentsQuery['items']>([]);
  bottomRowData$ = new BehaviorSubject<MonthCloseBottomData>(this.emptyBottomData());
  closedMonthColumns = [
    'forecast_obj',
    'adjustment_obj',
    'accrual_adjusted_obj',
    'invoice_amounts',
    'net_accruals',
    'eom_accruals',
    'eom_accrual_debit',
    'eom_accrual_credit',
    'eom_prepaid_debit',
    'eom_prepaid_credit',
    'eom_accrual',
    'eom_prepaid',
    'eom_unpaid',
    'initial_wp_obj',
    'monthly_wp_obj',
    'monthly_invoice_ltd',
  ] as const;
  closeMonthProcessingMessage =
    'The period close process is currently running. It can take up to 5 minutes. You can navigate away from this page. ' +
    'You will receive an email notification when complete, and an Audit Package will be available in the Document Library.';
  columnsForMonth = [
    'accrual_adjusted_obj',
    'invoice_amounts',
    'net_accruals',
    'eom_accruals',
    'eom_accrual_debit',
    'eom_accrual_credit',
    'eom_prepaid_debit',
    'eom_prepaid_credit',
    'monthly_wp_obj',
    'monthly_invoice_ltd',
    'eom_accrual',
    'eom_prepaid',
    'eom_unpaid',
  ] as const;
  currencies: Set<Currency> = new Set();
  currentMonth = '';
  currentMonthSignal = this.mainQuery.currentOpenMonth;
  getWorkflowDate?: string;
  getWorkflowSub?: Subscription;
  invoice_statuses: Array<InvoiceStatus> = [
    InvoiceStatus.STATUS_APPROVED,
    InvoiceStatus.STATUS_IN_QUEUE,
    InvoiceStatus.STATUS_PENDING_REVIEW,
    InvoiceStatus.STATUS_PENDING_APPROVAL,
  ];
  isCloseMonthsProcessing = this.eventQuery.selectProcessingEvent(EventType.CLOSE_TRIAL_MONTH);
  isQuarterCloseEnabled$ = this.workflowQuery.isQuarterCloseEnabled$;
  // when we change the trial we need to reset selectedQuarter
  // so this variable is holding the latest value for selected trial.
  lastSelectedQuarterValue: null | string = null;
  months: QuarterDate[] = [];
  quarter_end_data: QuarterEndData = {
    ending_accruals: 0,
    invoice_ltd: 0,
    invoice_unpaid_ltd: 0,
    wp_ltd: 0,
  };
  quarter_start_data: QuarterStartData = {
    invoice_ltd: 0,
    starting_accrual: 0,
    starting_prepaid: 0,
    wp_ltd: 0,
  };
  quartersObj: Record<string, QuarterDate[]> = {};
  quartersObjUpdated$ = new Subject();
  refresh$ = new Subject();
  refreshNgOnInit$ = new Subject();
  runningOnInit = false;
  selectedCategoryType$ = new BehaviorSubject<CategoryType>(CategoryType.CATEGORY_SERVICE);
  selectedMonth$ = new BehaviorSubject<null | {
    category: string;
    month: string;
    vendor: string;
  }>(null);
  // todo refactor move selectedQuarter to store and always use signal
  selectedQuarter = new FormControl('');
  trialAttributesLoading = this.globalStore.getLoading('TrialAttributes');
  trialAttributesLoading$ = toObservable(this.trialAttributesLoading);
  trialKey = this.mainQuery.getSelectedTrial()?.id || '';
  upperShorNameMonths = Utils.SHORT_MONTH_NAMES.map((month) => month.toUpperCase());

  // eslint-disable-next-line max-lines-per-function
  ngOnInit(): void {
    this.refreshNgOnInit$
      .pipe(
        startWith(null),
        takeUntilDestroyed(this.destroyRef),
        // eslint-disable-next-line max-lines-per-function
        switchMap(() => {
          this.organizationStore.setActive(null);

          return merge(
            this.authQuery.adminUser$.pipe(
              tap((event) => {
                this.periodCloseService.isAdminUser.next(event);
              })
            ),
            this.organizationService.getListWithTotalBudgetAmount().pipe(
              switchMap((vendorsWithBudget) => {
                this.invoicesLoading$.next(true);

                const uniqVendorCurrencies = uniq(
                  vendorsWithBudget.data
                    ?.filter(
                      (x) =>
                        x.organization_type === OrganizationType.ORGANIZATION_VENDOR &&
                        x.current_budget_versions.length > 0
                    )
                    .map((x) => x.currency)
                );

                this.numberOfVendorCurrencies.set(uniqVendorCurrencies.length);
                this.isVendorCurrenciesUSD.set(
                  uniqVendorCurrencies.every((currency) => currency === Currency.USD)
                );

                return combineLatest([
                  this.gqlService
                    .listInvoicesForReconciliation$({
                      deposit: false,
                      invoice_statuses: this.invoice_statuses,
                    })
                    .pipe(
                      map(({ data, errors, success }) => {
                        // Store the ungrouped invoices to support
                        // the Review Invoices, Prepaids, and Credit Memos checklist row.
                        // The checklist row requires knowing the number
                        // of invoices, and their current statuses.
                        this.periodCloseService.ungroupedInvoices = data || [];

                        this.invoices$.next(
                          groupBy(
                            (data || [])
                              .filter((invoice) => invoice.accrual_period)
                              .map(
                                ({
                                  accrual_period,
                                  expense_amounts,
                                  id,
                                  invoice_status,
                                  organization,
                                  payment_date,
                                }) => {
                                  return {
                                    accrual_period: accrual_period || '',
                                    expense_amounts:
                                      this.invoiceService.mapExpenseAmountToObject(expense_amounts),
                                    id,
                                    invoice_status: invoice_status || '',
                                    organization_id: organization.id,
                                    payment_date: payment_date || '',
                                  };
                                }
                              ),
                            'organization_id'
                          ) as Record<
                            string,
                            {
                              accrual_period: string;
                              expense_amounts: InvoiceAmounts;
                              id: string;
                              invoice_status: InvoiceStatus;
                              organization_id: string;
                              payment_date: string;
                            }[]
                          >
                        );

                        if (!(success && data)) {
                          this.overlayService.error(errors);
                        }

                        this.invoicesLoading$.next(false);
                      })
                    ),
                ]);
              })
            ),
            this.refresh$.pipe(
              startWith(null),
              switchMap(() => {
                return combineLatest([
                  this.selectedQuarter.valueChanges.pipe(
                    distinctUntilChanged(),
                    startWith(null),
                    switchMap((selectedQuarter) => {
                      this.selectedQuarterSignal.set(selectedQuarter || '');

                      if (selectedQuarter) {
                        this.updateIsCurrentQuarterSelected(selectedQuarter);
                      }

                      this.lastSelectedQuarterValue = selectedQuarter;
                      this.onSelectedQuarterChange(selectedQuarter as string);

                      return this.budgetService.getInMonthBudgetData(
                        selectedQuarter ? this.quartersObj[selectedQuarter][0].iso : null
                      );
                    })
                  ),
                  this.budgetService.getMonthCloseApprovals(),
                ]);
              })
            ),
            this.gridData$.pipe(
              tap((rows) => {
                const { obj } = this.generatePinnedBottomData(
                  rows,
                  <Currency>this.currencies.values().next().value
                );

                if (this.currencies.size === 1) {
                  this.bottomRowData$.next(obj);
                } else {
                  this.bottomRowData$.next(this.emptyBottomData());
                }
              })
            )
          );
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.mainStore.update({ fullPage: false });
    this.getWorkflowSub?.unsubscribe();
    this.periodCloseService.resetAllState();
  }

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    this.mainStore.update({ fullPage: true });

    this.eventQuery
      .selectProcessingEvent$(EventType.CLOSE_TRIAL_MONTH)
      .pipe(takeUntilDestroyed())
      .subscribe((isProcessing) => {
        if (!this.runningOnInit && isProcessing === false) {
          this.mainService.getOpenMonth$();
          void this.downloadAuditPackages();
          this.refreshGridData();
          this.timeline.refresh();
        }
      });

    this.eventService
      .select$(EventType.UPDATE_QUARTER_ACCRUALS)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (
          !this.runningOnInit &&
          !this.eventQuery.isEventProcessing(EventType.CLOSE_TRIAL_MONTH)
        ) {
          this.refreshGridData();
        }
      });

    this.mainQuery
      .select('trialKey')
      .pipe(
        switchMap(() => {
          this.currencies.clear();

          return this.downloadAuditPackages();
        })
      )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.bottomRowData$.next(this.emptyBottomData());
        this.lastSelectedQuarterValue = null;
      });

    combineLatest([
      this.selectedQuarter.valueChanges.pipe(startWith('')),
      this.quartersObjUpdated$.pipe(startWith(null)),
      this.trialAttributesLoading$,
      toObservable(this.currencyToggle),
    ])
      .pipe(
        switchMap(() => {
          const v = this.selectedQuarter.value;
          const primary = this.globalStore.primaryAttribute();
          const secondary = this.globalStore.secondaryAttribute();
          const trialAttributesLoading = this.trialAttributesLoading();

          if (!v || trialAttributesLoading) {
            return EMPTY;
          }

          this.newQCGridDataLoading.set(true);

          const trial_currency = this.currencyToggle() === CurrencyToggle.PRIMARY;

          const input: ListQuarterCloseExpensesInput = {
            attribute_group: primary.attribute_name
              ? JSON.stringify(
                  [
                    {
                      attribute_name: primary.attribute_name,
                    },
                    secondary.attribute_name
                      ? {
                          attribute_name: secondary.attribute_name,
                        }
                      : null,
                  ].filter(Boolean)
                )
              : undefined,
            current_quarter: v.replace('-', ' '),
            rollup_discount: true,
            trial_currency,
            vendor_id: null,
          };

          return forkJoin([
            this.gqlService.listQuarterCloseExpenses$(input),
            // if trial_currency is true, we also need to fetch old data for month stats
            trial_currency
              ? this.gqlService.listQuarterCloseExpenses$({ ...input, trial_currency: false })
              : of(null),
          ]);
        }),
        combineLatestWith(this.organizationQuery.selectAll()),
        takeUntilDestroyed()
      )
      .subscribe(([[{ data, errors, success }, queryWithoutTrialCurrency]]) => {
        if (success && data) {
          this.newQCGridData$.next(this.parseListQuarterCloseExpensesQuery(data));
        } else {
          this.newQCGridData$.next([]);
          console.error(errors);
        }

        if (queryWithoutTrialCurrency && queryWithoutTrialCurrency.data) {
          this.newQCGridDataWithoutTrialCurrency.set(
            this.parseListQuarterCloseExpensesQuery(queryWithoutTrialCurrency.data)
          );
        } else {
          this.newQCGridDataWithoutTrialCurrency.set(this.newQCGridData());
        }

        this.newQCGridDataLoading.set(false);

        this.setMonthStats();
      });
  }

  async downloadAuditPackages() {
    const files = await this.apiService.getFilesByFilters(
      `trials/${this.trialKey}/`,
      undefined,
      undefined,
      DocumentType.DOCUMENT_MONTHLY_AUDIT_PACKAGE,
      undefined
    );

    if (files) {
      this.auditPackages$.next(files);
    }
  }

  emptyBottomData() {
    return {
      account_value: '',
      accrual_adjusted_obj: {},
      adjustment_obj: {},
      attributes: [],
      dept_value: '',
      eom_accrual: {},
      eom_accrual_credit: {},
      eom_accrual_debit: {},
      eom_accruals: {},
      eom_prepaid: {},
      eom_prepaid_credit: {},
      eom_prepaid_debit: {},
      eom_unpaid: {},
      forecast_obj: {},
      initial_wp_obj: {},
      invoice_amounts: {},
      monthly_invoice_ltd: {},
      monthly_wp_obj: {},
      net_accruals: {},
      po_value: '',
    };
  }

  filterPackagesByMonth(date: string) {
    return this.auditPackages$
      .getValue()
      .filter((ap) => {
        if (ap.bucket_key && ap.name) {
          const bucket_key = ap.bucket_key.replace('public/', '');
          const dateForMonthlyAuditPackages = `${dayjs(date).format('MMM-YYYY')}`;
          const dateForAccrualSummary = `${dayjs(date).format('MMMM-YYYY').toLowerCase()}`;

          return (
            bucket_key.includes(dateForMonthlyAuditPackages) ||
            (bucket_key.toLowerCase().includes('accrual') &&
              (this.filterQuarterPackagesToMonths(dateForAccrualSummary, ap.name) ||
                bucket_key.includes(dateForAccrualSummary)))
          );
        }

        return false;
      })
      .map((val) => {
        return { ...val, bucket_key: val.bucket_key.replace('public/', '') };
      });
  }

  filterQuarterPackagesToMonths(date: string, fileName: string) {
    const dateLowerCase = date.toLowerCase();
    const fileNameLowerCase = fileName.toLowerCase();
    const yearCheck = fileName.includes(dayjs(date).year().toString());

    return (
      yearCheck &&
      ((dateLowerCase.includes('march') && fileNameLowerCase.includes('q1')) ||
        (dateLowerCase.includes('june') && fileNameLowerCase.includes('q2')) ||
        (dateLowerCase.includes('september') && fileNameLowerCase.includes('q3')) ||
        (dateLowerCase.includes('december') && fileNameLowerCase.includes('q4')))
    );
  }

  // eslint-disable-next-line max-lines-per-function
  generatePinnedBottomData(rows: MonthCloseTableRowData[], currency: Currency) {
    const data = rows.reduce(
      (acc, row) => {
        // eslint-disable-next-line complexity
        this.closedMonthColumns.forEach((field) => {
          for (const period of Object.keys(row[field])) {
            if (field === 'eom_accrual') {
              acc.eom_accruals_trial_currency[period] =
                (acc.eom_accruals_trial_currency[period] || 0) +
                (row.eom_accrual[period] || 0) * (row.exchange_rate[period] || 1);
              acc[field][period] = (acc[field][period] || 0) + (row[field][period] || 0);
            } else if (field === 'eom_prepaid') {
              acc.eom_prepaid_trial_currency[period] =
                (acc.eom_prepaid_trial_currency[period] || 0) +
                (row.eom_prepaid[period] || 0) * (row.exchange_rate[period] || 1);
              acc[field][period] = (acc[field][period] || 0) + (row[field][period] || 0);
            } else if (field === 'eom_unpaid') {
              acc.eom_unpaid_trial_currency[period] =
                (acc.eom_unpaid_trial_currency[period] || 0) +
                (row.eom_unpaid[period] || 0) * (row.exchange_rate[period] || 1);
              acc[field][period] = (acc[field][period] || 0) + (row[field][period] || 0);
            } else {
              acc[field][period] = (acc[field][period] || 0) + (row[field][period] || 0);
            }
          }
        });

        return acc;
      },
      {
        accrual_adjusted_obj: {},
        adjustment_obj: {},
        eom_accrual: {},
        eom_accrual_credit: {},
        eom_accrual_debit: {},
        eom_accruals: {},
        eom_accruals_trial_currency: {},
        eom_prepaid: {},
        eom_prepaid_credit: {},
        eom_prepaid_debit: {},
        eom_prepaid_trial_currency: {},
        eom_unpaid: {},
        eom_unpaid_trial_currency: {},
        forecast_obj: {},
        initial_wp_obj: {},
        invoice_amounts: {},
        monthly_invoice_ltd: {},
        monthly_wp_obj: {},
        net_accruals: {},
      } as {
        [k: string]: Record<string, number>;
        accrual_adjusted_obj: Record<string, number>;
        adjustment_obj: Record<string, number>;
        eom_accrual: Record<string, number>;
        eom_accrual_credit: Record<string, number>;
        eom_accrual_debit: Record<string, number>;
        eom_accruals: Record<string, number>;
        eom_accruals_trial_currency: Record<string, number>;
        eom_prepaid: Record<string, number>;
        eom_prepaid_credit: Record<string, number>;
        eom_prepaid_debit: Record<string, number>;
        eom_prepaid_trial_currency: Record<string, number>;
        eom_unpaid: Record<string, number>;
        eom_unpaid_trial_currency: Record<string, number>;
        forecast_obj: Record<string, number>;
        initial_wp_obj: Record<string, number>;
        invoice_amounts: Record<string, number>;
        monthly_invoice_ltd: Record<string, number>;
        monthly_wp_obj: Record<string, number>;
        net_accruals: Record<string, number>;
      }
    );

    const { ending_accruals, starting_accrual_balance } = rows.reduce(
      (acc, row) => {
        acc.ending_accruals += row.ending_accruals;
        acc.starting_accrual_balance += row.starting_accruals;

        return acc;
      },
      {
        ending_accruals: 0,
        starting_accrual_balance: 0,
      }
    );

    const analytics = {
      ending_accruals,
      initial_wp: 0,
      invoices_received: 0,
      starting_accrual_balance,
      total_adjustments: 0,
    };

    (this.quartersObj[this.selectedQuarter.value || ''] || []).forEach((month) => {
      analytics.initial_wp += data.initial_wp_obj[month.date] || 0;
      analytics.total_adjustments += data.adjustment_obj[month.date] || 0;
    });

    analytics.invoices_received = this.quarter_end_data.invoice_ltd;

    const obj = {
      ...data,
      account_value: '',
      cost_category: 'Total',
      currency,
      dept_value: '',
      ending_accruals: this.quarter_end_data.ending_accruals,
      invoice_ltd_end: this.quarter_end_data.invoice_ltd,
      invoice_ltd_start: this.quarter_start_data.invoice_ltd,
      invoice_unpaid_ltd_end: this.quarter_end_data.invoice_unpaid_ltd,
      po_value: '',
      starting_accrual: this.quarter_start_data.starting_accrual,
      starting_prepaid: this.quarter_start_data.starting_prepaid,
      wp_ltd_end: this.quarter_end_data.wp_ltd,
      wp_ltd_start: this.quarter_start_data.wp_ltd,
    };

    const eom_net_accrual_trial_currency: Record<string, number> = {};

    Object.keys(obj.eom_accruals_trial_currency).forEach((month) => {
      eom_net_accrual_trial_currency[month] =
        obj.eom_accruals_trial_currency[month] - obj.eom_prepaid_trial_currency[month];
    });

    return {
      analytics,
      obj,
    };
  }

  getBudgetDataWithSummary(data: MonthCloseTableRowData[], quarterMonths: QuarterDate[]) {
    return data.map((row) => {
      return this.columnsForMonth.reduce((accum, rowKey) => {
        return <MonthCloseTableRowData>{
          ...accum,
          [rowKey]: {
            ...row[rowKey],
            total: ['eom_accrual', 'eom_accruals', 'eom_prepaid', 'eom_unpaid'].includes(rowKey)
              ? accum[rowKey][quarterMonths[quarterMonths.length - 1].date]
              : quarterMonths.reduce((sum, { date }) => {
                  sum += accum[rowKey][date] || 0;

                  return sum;
                }, 0),
          },
        };
      }, row);
    });
  }

  isCloseSummaryVisible() {
    return !this.router.url.includes(ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX);
  }

  // eslint-disable-next-line max-lines-per-function
  mapFn(
    {
      approval_state,
      budget_state,
      groupedInvoices,
      user,
    }: {
      approval_state: ApprovalState;
      budget_state: BudgetState;
      groupedInvoices: Record<
        string,
        {
          accrual_period: string;
          expense_amounts: InvoiceAmounts;
          id: string;
          invoice_status: InvoiceStatus;
          organization_id: string;
          payment_date: string;
        }[]
      >;
      user: LoggedInUser | null;
    },
    mergeDiscountWithServices = false
  ) {
    const { budget_data, budget_info, header_data } = budget_state;

    if (!budget_info?.[0]) {
      return [];
    }

    const { current_month } = budget_info[0];

    let quarters: string[] = [];
    const quartersObj: Record<
      string,
      {
        date: string;
        iso: string;
        parsedDate: dayjs.Dayjs;
      }[]
    > = {};
    let currentQuarter = '';

    header_data
      .filter((x) => x.group_name === 'Timeline')[0]
      .date_headers.forEach((date) => {
        const parsedDate = this.parseBudgetHeaderDate(date);
        const q = parsedDate.quarter();
        const year = parsedDate.year();
        const str = `Q${q}-${year}`;

        quartersObj[str] ||= [];
        const iso = parsedDate.format('YYYY-MM-DD');

        if (current_month?.slice(0, 7) === iso.slice(0, 7)) {
          currentQuarter = str;
        }

        quartersObj[str].push({
          date,
          iso,
          parsedDate,
        });
        quarters.push(str);
      });
    quarters = uniq(quarters);

    const auxilius_start_date = this.mainQuery.getAuxiliusStartDate();

    const current_quarter_index = quarters.findIndex((q) => q === currentQuarter);

    this.quarters.set(quarters.slice(0, current_quarter_index + 1).reverse());

    if (auxilius_start_date) {
      this.quarters.set(
        this.quarters().filter((q) => {
          const monthIndex = quartersObj[q].length - 1;

          return (
            quartersObj[q][monthIndex].parsedDate.isSameOrAfter(auxilius_start_date, 'month') ||
            quartersObj[q][monthIndex].parsedDate.isSameOrAfter(current_month, 'month')
          );
        })
      );
    }

    this.quartersObj = quartersObj;
    this.currentQuarter.set(currentQuarter);
    this.currentMonth = current_month || '';

    if (!this.lastSelectedQuarterValue) {
      this.selectedQuarter.setValue(currentQuarter, { emitEvent: false });
      this.selectedQuarterSignal.set(currentQuarter);
      this.updateIsCurrentQuarterSelected(currentQuarter);
      this.onSelectedQuarterChange(currentQuarter);
    }

    this.quartersObjUpdated$.next(null);

    this.quarter_start_data = {
      invoice_ltd: 0,
      starting_accrual: 0,
      starting_prepaid: 0,
      wp_ltd: 0,
    };

    this.quarter_end_data = {
      ending_accruals: 0,
      invoice_ltd: 0,
      invoice_unpaid_ltd: 0,
      wp_ltd: 0,
    };

    const newBudgetData = !mergeDiscountWithServices
      ? budget_data
      : this.mergeServicesWithDiscount(budget_data);

    const months = this.quartersObj[this.selectedQuarter.value || ''];

    const firstMonth = dayjs(months[0].iso).startOf('quarter');
    const secondMonth = firstMonth.add(1, 'month');
    const thirdMonth = firstMonth.add(2, 'month');
    // some quarters don't have 3 months, so we are adding them manually
    const quarterMonths: QuarterDate[] = [
      {
        date: firstMonth.format('MMM-YYYY').toUpperCase(),
        iso: firstMonth.format('YYYY-MM-DD'),
        parsedDate: firstMonth,
      },
      {
        date: secondMonth.format('MMM-YYYY').toUpperCase(),
        iso: secondMonth.format('YYYY-MM-DD'),
        parsedDate: secondMonth,
      },
      {
        date: thirdMonth.format('MMM-YYYY').toUpperCase(),
        iso: thirdMonth.format('YYYY-MM-DD'),
        parsedDate: thirdMonth,
      },
    ];
    const timeline_start = quarterMonths[0].iso;

    // eslint-disable-next-line max-lines-per-function, complexity
    const data: MonthCloseTableRowData[] = newBudgetData.map((row) => {
      this.currencies.add((row.contract_direct_cost_currency as Currency) || Currency.USD);
      const approvals: Approval[] = [];
      const usedApprovals: Approval[] = [];
      const arr: {
        checked: boolean;
        disabled: boolean;
        name: string;
        permission: string;
      }[] = [];
      const rules = this.launchDarklyService.flags$.getValue().approval_rule_in_month.rule;

      if (approval_state.approvals) {
        approval_state.approvals.forEach((approval) => {
          if (row.vendor_id === approval.vendor_id && row.cost_category === approval.category) {
            approvals.push(approval);
          }
        });
      }

      for (let i = 0; i < rules.length; i++) {
        let checked = false;
        let disabled = false;
        const { name, permission_type } = rules[i];

        for (let index = 0; index < approvals.length; index++) {
          if (approvals[index].permission === permission_type) {
            usedApprovals.push(...approvals.splice(index));
            checked = true;
            disabled = true;
            break;
          }
        }

        if (!checked && user && !user.IsSysAdmin()) {
          const isAuthorized = this.authService.isAuthorizedSync(user, {
            permissions: [permission_type],
          });

          if (isAuthorized) {
            disabled = usedApprovals.some(({ aux_user_id, permission }) => {
              return aux_user_id === user.getSub() && permission === permission_type;
            });
          } else {
            disabled = true;
          }
        }

        arr.push({
          checked,
          disabled: checked || disabled,
          name,
          permission: permission_type,
        });
      }

      const obj = arr.reduce(
        (acc, { checked, disabled, name, permission }) => {
          acc[`${name}::${permission}`] = checked;
          acc[`${name}::${permission}::disabled`] = disabled;

          return acc;
        },
        {} as Record<string, boolean>
      );

      // const date = budget_info[0].current_month?.slice(0, 7);
      const invoices = groupedInvoices[row.vendor_id || ''] || [];

      let invoice_ltd_start = 0;

      let invoice_ltd_end = 0;

      const eom_unpaid: Record<string, number> = {};

      const invoice_amounts = invoices.reduce(
        (i_acc, val) => {
          const period = dayjs(val.accrual_period).format('MMM-YYYY').toUpperCase();
          const is_old = dayjs(timeline_start).diff(val.accrual_period) > 0;

          let total = 0;

          if (row.cost_category === 'Services') {
            const discountValue = mergeDiscountWithServices
              ? val.expense_amounts.discount_total.value
              : 0;

            total = val.expense_amounts.services_total.value + discountValue;
            i_acc[period] = (i_acc[period] || 0) + total;

            if (is_old) {
              invoice_ltd_start += total;
            }
          }

          if (row.cost_category === 'Discount' && !mergeDiscountWithServices) {
            total = val.expense_amounts.discount_total.value;
            i_acc[period] = (i_acc[period] || 0) + total;

            if (is_old) {
              invoice_ltd_start += total;
            }
          }

          if (row.cost_category === 'Investigator') {
            total = val.expense_amounts.investigator_total.value;
            i_acc[period] = (i_acc[period] || 0) + total;

            if (is_old) {
              invoice_ltd_start += total;
            }
          }

          if (row.cost_category === 'Pass-through') {
            total = val.expense_amounts.pass_thru_total.value;
            i_acc[period] = (i_acc[period] || 0) + total;

            if (is_old) {
              invoice_ltd_start += total;
            }
          }

          if (val.invoice_status !== InvoiceStatus.STATUS_DECLINED) {
            quarterMonths.forEach((mon) => {
              const mPeriod = dayjs(mon.iso).format('MMM-YYYY').toUpperCase();
              const isSameBefore = dayjs(mon.iso)
                .date(1)
                .isSameOrAfter(dayjs(val.accrual_period).date(1));

              if (isSameBefore) {
                if (val.payment_date) {
                  const isPaySameBefore = dayjs(mon.iso)
                    .date(1)
                    .isSameOrAfter(dayjs(val.payment_date).date(1));

                  if (isPaySameBefore) {
                    return;
                  }
                }

                eom_unpaid[mPeriod] = (eom_unpaid[mPeriod] || 0) + total;
              }
            });
          }

          return i_acc;
        },
        {} as Record<string, number>
      );

      let forecast_obj = row.forecast_obj as Record<string, number>;
      const adjustment_obj = row.adjustment_obj as Record<string, number>;
      const initial_wp_obj: Record<string, number> = {};
      const monthly_wp_obj: Record<string, number> = {};
      const monthly_invoice_ltd: Record<string, number> = {};

      const accrual_obj = cloneDeep(row.accrual_obj) as Record<string, number>;
      const accrual_adjusted_obj = cloneDeep(row.accrual_adjusted_obj) as Record<string, number>;

      Object.keys(adjustment_obj).forEach((month) => {
        if (!accrual_obj[month]) {
          accrual_obj[month] = 0;
        }

        if (!accrual_adjusted_obj[month]) {
          accrual_adjusted_obj[month] = 0;
        }
      });

      if (row.cost_category === 'Investigator') {
        const investigator_forecast_obj: Record<string, number> = {};

        forecast_obj = { ...forecast_obj, ...investigator_forecast_obj };
      }

      let wp_ltd_end = row.wp_ltd;
      const starting_accruals = row.wp_ltd - invoice_ltd_start;
      const starting_accrual = starting_accruals > 0 ? starting_accruals : 0;
      const starting_prepaid = starting_accruals > 0 ? 0 : Math.abs(starting_accruals);

      this.quarter_start_data.wp_ltd += row.wp_ltd;
      this.quarter_start_data.invoice_ltd += invoice_ltd_start;
      this.quarter_start_data.starting_accrual += starting_accrual;
      this.quarter_start_data.starting_prepaid += starting_prepaid;

      quarterMonths.forEach((month) => {
        initial_wp_obj[month.date] =
          accrual_obj && Object.prototype.hasOwnProperty.call(accrual_obj, month.date)
            ? accrual_obj[month.date]
            : forecast_obj[month.date];

        if (dayjs(month.iso).date(1).isAfter(dayjs(current_month).date(1))) {
          accrual_adjusted_obj[month.date] = initial_wp_obj[month.date];
          adjustment_obj[month.date] = 0;
        }

        wp_ltd_end += accrual_adjusted_obj[month.date] ? accrual_adjusted_obj[month.date] : 0;
      });

      const eom_accruals: Record<string, number> = {};
      const eom_accrual: Record<string, number> = {};
      const eom_prepaid: Record<string, number> = {};
      const eom_accrual_debit: Record<string, number> = {};
      const eom_accrual_credit: Record<string, number> = {};
      const eom_prepaid_debit: Record<string, number> = {};
      const eom_prepaid_credit: Record<string, number> = {};
      const net_accruals = Object.keys(accrual_adjusted_obj).reduce(
        (acc, val) => {
          return {
            ...acc,
            [val]: (row.accrual_adjusted_obj[val] || 0) - (invoice_amounts[val] || 0),
          };
        },
        {} as Record<string, number>
      );

      quarterMonths.forEach((month, index) => {
        invoice_ltd_end += invoice_amounts[month.date] || 0;

        eom_accruals[month.date] =
          starting_accruals +
          quarterMonths.slice(0, index + 1).reduce((acc, m) => {
            return acc + (net_accruals[m.date] || 0);
          }, 0);

        eom_accrual[month.date] = eom_accruals[month.date] > 0 ? eom_accruals[month.date] : 0;
        eom_prepaid[month.date] =
          eom_accruals[month.date] > 0 ? 0 : Math.abs(eom_accruals[month.date]);
      });

      this.months = months;

      invoice_ltd_end += invoice_ltd_start;
      const invoice_unpaid_ltd_end = eom_unpaid[last(quarterMonths)?.date || ''] || 0;

      const ending_accruals = wp_ltd_end - invoice_ltd_end;

      this.quarter_end_data.invoice_unpaid_ltd += invoice_unpaid_ltd_end;
      this.quarter_end_data.ending_accruals += ending_accruals;
      this.quarter_end_data.wp_ltd += wp_ltd_end;
      this.quarter_end_data.invoice_ltd += invoice_ltd_end;

      const account_value =
        row?.attributes?.find((x) => x.attribute === 'account_no')?.attribute_value || '';

      const po_value = row?.attributes?.find((x) => x.attribute === 'po_no')?.attribute_value || '';

      const dept_value =
        row?.attributes?.find((x) => x.attribute === 'department')?.attribute_value || '';

      const extraAttributes =
        row.attributes?.reduce(
          (acc, a) => {
            if (a.attribute_name && a.attribute_value) {
              acc[getEncodedAttributeName(a.attribute_name)] = a.attribute_value;
            }

            return acc;
          },
          {} as Record<string, string>
        ) || {};

      quarterMonths.forEach((month, index) => {
        const wp = accrual_adjusted_obj[month.date] || 0;
        const ir = invoice_amounts[month.date] || 0;

        if (index === 0) {
          //first month of the quarter
          monthly_wp_obj[month.date] = (row.wp_ltd || 0) + wp;
          monthly_invoice_ltd[month.date] = (invoice_ltd_start || 0) + ir;
        } else {
          //second month of the quarter
          monthly_wp_obj[month.date] = monthly_wp_obj[quarterMonths[index - 1].date] + wp;
          monthly_invoice_ltd[month.date] = monthly_invoice_ltd[quarterMonths[index - 1].date] + ir;
        }
      });

      const monthStart: Record<string, number> = {};
      const monthEnd: Record<string, number> = {};

      quarterMonths.forEach((month, index) => {
        if (index === 0) {
          monthStart[month.date] = starting_accruals || 0;
        } else {
          monthStart[month.date] = monthEnd[quarterMonths[index - 1].date];
        }

        monthEnd[month.date] = monthStart[month.date] + (net_accruals[month.date] || 0);
      });

      const getPrepaidAndAccrualAccountBalance = (netAccrual: number) => {
        return {
          accrual: netAccrual > 0 ? netAccrual : 0,
          prepaid: netAccrual < 0 ? -netAccrual : 0,
        };
      };

      quarterMonths.forEach((month) => {
        const netAccrualAtMonthStart = monthStart[month.date];
        const netAccrualAtMonthClose = monthEnd[month.date];
        const startingAccountBalances = getPrepaidAndAccrualAccountBalance(netAccrualAtMonthStart);
        const endingAccountBalances = getPrepaidAndAccrualAccountBalance(netAccrualAtMonthClose);
        const deltaPrepaid = endingAccountBalances.prepaid - startingAccountBalances.prepaid;
        const deltaAccrual = endingAccountBalances.accrual - startingAccountBalances.accrual;

        eom_accrual_credit[month.date] = deltaAccrual > 0 ? deltaAccrual : 0;
        eom_accrual_debit[month.date] = deltaAccrual < 0 ? -deltaAccrual : 0;
        eom_prepaid_credit[month.date] = deltaPrepaid < 0 ? -deltaPrepaid : 0;
        eom_prepaid_debit[month.date] = deltaPrepaid > 0 ? deltaPrepaid : 0;
      });

      return <MonthCloseTableRowData>{
        ...row,
        ...obj,
        account_value,
        adjustment_obj,
        dept_value,
        ending_accruals,
        eom_accrual,
        eom_accrual_credit,
        eom_accrual_debit,
        eom_accruals,
        eom_prepaid,
        eom_prepaid_credit,
        eom_prepaid_debit,
        eom_unpaid,
        forecast_obj,
        initial_wp_obj,
        invoice_amounts,
        invoice_ltd_end,
        invoice_ltd_start,
        invoice_unpaid_ltd_end,
        monthly_invoice_ltd,
        monthly_wp_obj,
        net_accruals,
        po_value,
        starting_accrual,
        starting_accruals,
        starting_prepaid,
        wp_ltd_end,
        wp_ltd_start: row.wp_ltd,
        ...extraAttributes,
      };
    });

    return this.getBudgetDataWithSummary(data, quarterMonths);
  }

  mergeServicesWithDiscount(budget_data: ExtendedBudgetData[]) {
    const discountRows: Record<string, ExtendedBudgetData> = {};

    return budget_data
      .filter((row) => {
        if (row.cost_category === 'Discount') {
          discountRows[row.vendor_id || ''] = row;

          return false;
        }

        return true;
      })
      .map((row) => {
        if (row.cost_category === 'Services' && row.vendor_id && discountRows[row.vendor_id]) {
          const discountRow = discountRows[row.vendor_id];
          const keys = [
            'direct_cost',
            'contract_direct_cost',
            'accrual',
            'forecast',
            'total_monthly_accrual',
            'adjustment',
            'wp_ltd',
          ] as const;
          const obj_keys = [
            'forecast_obj',
            'accrual_obj',
            'accrual_adjusted_obj',
            'adjustment_obj',
            'accrual_override',
          ] as const;

          return {
            ...row,
            ...keys.reduce(
              (acc, key) => {
                acc[key] = (row[key] || 0) + (discountRow[key] || 0);

                return acc;
              },
              {} as ObjectFromList<typeof keys, number>
            ),
            ...obj_keys.reduce(
              (acc, key) => {
                acc[key] = {};
                Object.entries(row[key]).forEach(([month, v]) => {
                  acc[key][month] = v + (discountRow[key][month] || 0);
                });

                return acc;
              },
              {} as Record<string, Record<string, number>>
            ),
          };
        }

        return row;
      });
  }

  onSelectedQuarterChange(selectedQuarter: string): void {
    if (selectedQuarter) {
      const workflowDate = dayjs(this.periodCloseService.getSelectedQuarterMonth(this.router.url))
        .format('MMM-YYYY')
        .toUpperCase();

      if (this.getWorkflowDate === workflowDate) {
        return;
      }

      this.getWorkflowSub?.unsubscribe();
      this.getWorkflowDate = workflowDate;

      if (!this.router.url.includes(ROUTING_PATH.CLOSING.CHECKLIST)) {
        this.getWorkflowSub = this.workflowService.getWorkflowList(workflowDate).subscribe();
      }
    }
  }

  onSelectionChanged({ column, node: selectedRow }: ICellRendererParams) {
    const period = column?.getColId().split('_')[0];

    if (!period) {
      return;
    }

    if (selectedRow.group) {
      this.organizationStore.setActive(selectedRow.key);
      // When vendor's pencil selected, this'll be unsetable.
      this.selectedCategoryType$.next(CategoryType.CATEGORY_UNDETERMINED);
    } else {
      const data: BudgetData = selectedRow.data;

      this.organizationStore.setActive(data.vendor_id || '');
      this.selectedCategoryType$.next(
        `CATEGORY_${data.cost_category?.replace('AMOUNT_', '')}` as CategoryType
      );
    }

    const month =
      this.quartersObj[this.selectedQuarter.value || ''][+period.replace('qmonth', '')[0] - 1];

    this.selectedMonth$.next({
      category: this.selectedCategoryType$.getValue(),
      month: month.parsedDate.date(1).format('YYYY-MM-DD'),
      vendor: this.organizationQuery.getActiveId() || '',
    });

    void this.router.navigate([ROUTING_PATH.CLOSING.INDEX, ROUTING_PATH.CLOSING.EXPENSE_DETAIL]);
  }

  private parseBudgetHeaderDate(date: string): dayjs.Dayjs {
    const [month, year] = date.split('-');

    const monthNumber = this.upperShorNameMonths.indexOf(month) + 1;
    const strMonth = `${monthNumber}`.padStart(2, '0');

    return dayjs(`03/${strMonth}/${year}`, 'DD/MM/YYYY');
  }

  parseListQuarterCloseExpensesQuery(data: listQuarterCloseExpensesQuery[]) {
    return data.map((row) => {
      let activity_name = row.cost_category;

      switch (row.cost_category) {
        case AmountType.AMOUNT_INVESTIGATOR:
          activity_name = 'Investigator';
          break;
        case AmountType.AMOUNT_PASSTHROUGH:
          activity_name = 'Pass-through';
          break;
        case AmountType.AMOUNT_SERVICE:
          activity_name = 'Services';
          break;
        default:
          break;
      }

      const attributes = JSON.parse(row.attributes || '[]') as QCGridData['attributes'];
      const groupValues = JSON.parse(JSON.parse(row.group_value || '"{}"'));

      const extraAttributes =
        attributes.reduce(
          (acc, a) => {
            if (a.attribute_name && a.attribute_value) {
              acc[getEncodedAttributeName(a.attribute_name)] = a.attribute_value;
            }

            return acc;
          },
          {} as Record<string, string>
        ) || {};

      const groupAttributes = Object.keys(groupValues).reduce(
        (acc, key) => {
          acc[getEncodedAttributeName(key)] = groupValues[key];

          return acc;
        },
        {} as Record<string, string>
      );

      return {
        ...row,
        activity_name,
        attributes,
        ...extraAttributes,
        ...groupAttributes,
        currency:
          this.currencyToggle() === CurrencyToggle.CONTRACTED
            ? this.organizationQuery.getEntity(row.vendor_id)?.currency || Currency.USD
            : Currency.USD,
      };
    });
  }

  refreshGridData() {
    this.runningOnInit = true;
    setTimeout(() => {
      this.monthClosed.set(false);
      this.refreshNgOnInit$.next(null);
      this.runningOnInit = false;
    }, 5000);
  }

  private setMonthStats() {
    const data = this.newQCGridDataWithoutTrialCurrency();
    const months = data.reduce(
      (acc, row) => {
        return {
          month1: acc.month1 + (row.qmonth1_net_position_tc || 0),
          month2: acc.month2 + (row.qmonth2_net_position_tc || 0),
          month3: acc.month3 + (row.qmonth3_net_position_tc || 0),
        };
      },
      {
        month1: 0,
        month2: 0,
        month3: 0,
      }
    );
    const monthsArr = [months.month1, months.month2, months.month3];

    const currentMonth = dayjs(this.currentMonth);

    const newMonthStats = this.months.map(({ date }, index) => {
      let status = 'Open';

      const parserDateFormat = date.charAt(0).toUpperCase() + date.slice(1).toLowerCase();
      const month = dayjs(parserDateFormat, 'MMM-YYYY');

      if (month.isBefore(currentMonth, 'month')) {
        status = 'Closed';
      }

      if (month.isAfter(currentMonth, 'month')) {
        status = 'Future';
      }

      return <MonthStats>{
        date: dayjs(`01-${date}`).toISOString(),
        eom_accruals: monthsArr[index],
        status: status as MonthStats['status'],
      };
    });

    const areAllMonthsClosed = newMonthStats.every((month) => month.status === 'Closed');
    const monthsWithPackages = newMonthStats.map((monthStat) => {
      return {
        ...monthStat,
        allOfThemClosed: areAllMonthsClosed,
        auditPackages: this.filterPackagesByMonth(monthStat.date),
      };
    });

    this.monthsStat.set(monthsWithPackages);
  }

  updateIsCurrentQuarterSelected(selectedQuarter: string) {
    const isCurrentQuarterSelected = selectedQuarter === this.currentQuarter();

    this.periodCloseService.selectedQuarterMonth = isCurrentQuarterSelected
      ? this.currentMonth
      : last(this.quartersObj[selectedQuarter])?.iso || '';

    this.periodCloseService.persistedQuarterMonth = '';

    this.periodCloseService.currentOpenMonth = this.currentMonth;

    this.periodCloseService.quarterMonths = this.quartersObj[this.selectedQuarter.value || ''];

    this.periodCloseService.isCurrentQuarterSelected.next(isCurrentQuarterSelected);
  }
}
